import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { z } from 'zod'

import {
  mapQueries,
  TSearchKeywordResponse,
  ZLocationMarkerType,
  ZSubwayLine,
  ZSubwayMarker,
} from '@/entities/map'

import { convertMarkerToLocation } from '../lib/location'

export const ZLocation = ZSubwayMarker.pick({
  name: true,
  lat: true,
  lng: true,
  nextZoomLevel: true,
}).extend({
  subwayLines: z.array(ZSubwayLine).nullable(),
  keyword: z.string(),
  type: ZLocationMarkerType,
})

export type TLocation = z.infer<typeof ZLocation>

type Props = {
  keyword: string
}

export const useSelectedLocation = ({ keyword }: Props) => {
  const [selectedLocation, setSelectedLocation] = useState<TLocation | null>(
    null,
  )

  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()

  const fetchKeywordList = async () => {
    const response = await queryClient.fetchQuery(
      mapQueries.searchKeywordList({
        keyword,
        maxSize: 30,
      }),
    )

    return response
  }

  const findKeywordQueryCache = () => {
    const query = queryCache.find({
      type: 'active',
      queryKey: [...mapQueries.searchKeywordLists(), { keyword, maxSize: 30 }],
      exact: false,
    })

    if (!query) return null

    return query.state.data as TSearchKeywordResponse
  }

  const getFirstLocation = (response: TSearchKeywordResponse) => {
    const [regionLocation] = response.regions

    if (regionLocation) {
      return convertMarkerToLocation(regionLocation)
    }

    const [subwayLocation] = response.subways

    if (subwayLocation) {
      return convertMarkerToLocation(subwayLocation)
    }

    const [landmarkLocation] = response.landmarks

    if (landmarkLocation) {
      return convertMarkerToLocation(landmarkLocation)
    }

    return null
  }

  const getLocationByKeyword = async () => {
    if (selectedLocation) return selectedLocation

    const query = findKeywordQueryCache()

    if (query) {
      return getFirstLocation(query)
    }

    const response = await fetchKeywordList()

    if (response) {
      return getFirstLocation(response)
    }

    return null
  }

  const updateLocation = (location: TLocation | null) => {
    setSelectedLocation(location)
  }

  const resetLocation = () => {
    setSelectedLocation(null)
  }

  return {
    selectedLocation,
    getLocationByKeyword,
    updateLocation,
    resetLocation,
  }
}
